import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Campaign {
  id: string;
  advertiser_id: string;
  name: string;
  description: string | null;
  campaign_type: 'cpa' | 'cpl' | 'cps';
  category?: string;
  location?: string;
  image_url?: string;
  payout_amount: number;
  payout_model: 'fixed' | 'percentage';
  status: 'active' | 'paused' | 'ended';
  budget: number | null;
  spent: number;
  start_date: string;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  // Verification fields (from public view)
  fundraiser_verified?: boolean | null;
  fundraiser_name?: string | null;
  fundraiser_company?: string | null;
  fundraiser_type?: string | null;
  is_fully_verified?: boolean | null;
}

export const useCampaigns = (advertiserId?: string) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      
      if (advertiserId) {
        // Advertiser viewing their own campaigns - use campaigns table
        const { data, error } = await supabase
          .from('campaigns')
          .select('*')
          .eq('advertiser_id', advertiserId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setCampaigns((data as Campaign[]) || []);
      } else {
        // Public view - use public_campaigns view (includes verification status)
        const { data, error } = await supabase
          .from('public_campaigns')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        // Map to Campaign type (view has subset of fields plus verification)
        setCampaigns((data ?? []).map(campaign => ({
          ...campaign,
          id: campaign.id || '',
          name: campaign.name || '',
          description: campaign.description,
          campaign_type: (campaign.campaign_type || 'cpa') as 'cpa' | 'cpl' | 'cps',
          category: campaign.category,
          location: campaign.location,
          image_url: campaign.image_url,
          status: (campaign.status || 'active') as 'active' | 'paused' | 'ended',
          budget: campaign.budget,
          spent: campaign.spent ?? 0,
          start_date: campaign.start_date || '',
          end_date: campaign.end_date,
          created_at: campaign.created_at || '',
          payout_amount: 0,
          payout_model: 'fixed' as const,
          advertiser_id: '',
          updated_at: campaign.created_at || '',
          // Include verification fields from public view
          fundraiser_name: campaign.fundraiser_name,
          fundraiser_company: campaign.fundraiser_company,
          fundraiser_type: campaign.fundraiser_type,
          fundraiser_verified: campaign.fundraiser_verified,
          is_fully_verified: campaign.is_fully_verified,
        })));
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaigns',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [advertiserId]);

  return { campaigns, loading, refetch: fetchCampaigns };
};
